import React, { useState, useEffect, useMemo } from "react";
import Api from "../../services/Api";
import { API_FALECIDOS_FOLDER } from "../../constants";

const PAGE_SIZE = 5;

function formatDate(d) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("pt-BR");
}

export default function CorrecoesModal({ isOpen, onClose, onFalecidoUpdated }) {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [detail, setDetail] = useState(null); // item selecionado para detalhe

  useEffect(() => {
    if (!isOpen) return;
    setDetail(null);
    Api.getCorrecoes()
      .then((res) => setData(res.filter((item) => item.status === "pendente" || item.status == null)))
      .catch((err) => console.error("Erro ao buscar correções:", err));
  }, [isOpen]);

  const totalPages = Math.max(1, Math.ceil(data.length / PAGE_SIZE));

  const currentData = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return data.slice(start, start + PAGE_SIZE);
  }, [data, currentPage]);

  if (!isOpen) return null;

  const updateStatus = (item, status) => {
    setData((prev) =>
      prev.map((d) =>
        d.id === item.id
          ? { ...d, status: d.status === status ? "pendente" : status }
          : d
      )
    );
    // Atualiza também o item de detalhe se estiver aberto
    if (detail?.id === item.id) {
      setDetail((prev) => ({
        ...prev,
        status: prev.status === status ? "pendente" : status,
      }));
    }
  };

  const handleFinalizar = async () => {
    try {
      const resolvidos = data.filter((item) => item.status === "resolvido");
      const rejeitados = data.filter((item) => item.status === "rejeitado");

      const falecidosAtualizados = [];

      await Promise.all([
        ...resolvidos.map(async (item) => {
          falecidosAtualizados.push(item.falecido_id);
          return Api.resolverCorrecao(item.id, "resolvido");
        }),
        ...rejeitados.map((item) => Api.resolverCorrecao(item.id, "rejeitado")),
      ]);

      if (falecidosAtualizados.length > 0) {
        onFalecidoUpdated?.(falecidosAtualizados);
      }

      onClose();
    } catch (err) {
      console.error("Erro ao finalizar correções:", err);
    }
  };

  // ── VIEW DETALHE ────────────────────────────────────────────────────────────
  if (detail) {
    const statusDetail = data.find((d) => d.id === detail.id) || detail;
    return (
      <div style={overlay}>
        <div style={{ ...modal, maxWidth: 540 }}>
          {/* HEADER */}
          <div style={header}>
            <button
              onClick={() => setDetail(null)}
              style={{ ...closeButton, fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}
            >
              ← Voltar
            </button>
            <h3 style={{ margin: 0, fontSize: 15 }}>Detalhe da solicitação</h3>
            <button onClick={onClose} style={closeButton}>✕</button>
          </div>

          {/* CORPO */}
          <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>
            <Section label="Descrição" value={detail.descricao} />

            <div style={divider} />

            <p style={sectionTitle}>Dados sugeridos</p>
            <Section label="Nome sugerido" value={detail.nome_sugerido} />
            <Section label="Data de nascimento" value={formatDate(detail.data_nascimento_sugerida)} />
            <Section label="Data de falecimento" value={formatDate(detail.data_falecimento_sugerida)} />

            {detail.foto_url && (
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>Foto sugerida</p>
                <img
                  src={`${API_FALECIDOS_FOLDER}/${detail.foto_url}`}
                  alt="foto sugerida"
                  style={{ width: 80, height: 80, borderRadius: 8, objectFit: "cover", border: "1px solid #eee" }}
                />
              </div>
            )}

            <div style={divider} />

            <p style={sectionTitle}>Descrição da correção</p>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#444", lineHeight: 1.6, margin: 0 }}>
              {detail.descricao || "—"}
            </p>
          </div>

          {/* FOOTER — ações */}
          <div style={{ ...footer, justifyContent: "flex-end", gap: 10 }}>
            <button
              title="Aceitar correção"
              style={{
                ...actionBtn,
                ...(statusDetail.status === "resolvido" ? approvedStyle : inactiveStyle),
              }}
              onClick={() => updateStatus(detail, "resolvido")}
            >
              ✓ Aceitar
            </button>
            <button
              title="Rejeitar correção"
              style={{
                ...actionBtn,
                ...(statusDetail.status === "rejeitado" ? rejectedStyle : inactiveStyle),
              }}
              onClick={() => updateStatus(detail, "rejeitado")}
            >
              ✕ Rejeitar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── VIEW LISTA ──────────────────────────────────────────────────────────────
  return (
    <div style={overlay}>
      <div style={modal}>
        {/* HEADER */}
        <div style={header}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Solicitações de correção</h3>
          <button onClick={onClose} style={closeButton}>✕</button>
        </div>

        {/* TABELA */}
        <div style={{ flex: 1, overflow: "auto" }}>
          <table style={table}>
            <thead>
              <tr>
                <th style={{ ...th, width: "20%" }}>Falecido</th>
                <th style={{ ...th, width: "20%" }}>Solicitante</th>
                <th style={{ ...th, width: "20%" }}>Contato</th>
                <th style={{ ...th, width: "30%" }}>Descrição</th>
                <th style={{ ...th, width: "10%", textAlign: "center" }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {currentData.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ ...td, textAlign: "center", color: "#aaa" }}>
                    Nenhuma solicitação pendente.
                  </td>
                </tr>
              ) : (
                currentData.map((item, index) => (
                  <tr
                    key={item.id ?? index}
                    style={{ cursor: "pointer" }}
                    onClick={() => setDetail(item)}
                  >
                    <td style={td}>{item.falecido_nome || "-"}</td>
                    <td style={td}>{item.nome_solicitante || "-"}</td>
                    <td style={td}>{item.contato || "-"}</td>
                    <td style={{ ...td, color: item.descricao ? "#444" : "#ccc" }}>
                      {item.descricao || "—"}
                    </td>
                    <td style={td} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                        <button
                          title="Aceitar"
                          style={{
                            ...statusButton,
                            ...(item.status === "resolvido" ? approvedStyle : inactiveStyle),
                          }}
                          onClick={(e) => { e.stopPropagation(); updateStatus(item, "resolvido"); }}
                        >
                          ✓
                        </button>
                        <button
                          title="Rejeitar"
                          style={{
                            ...statusButton,
                            ...(item.status === "rejeitado" ? rejectedStyle : inactiveStyle),
                          }}
                          onClick={(e) => { e.stopPropagation(); updateStatus(item, "rejeitado"); }}
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* FOOTER */}
        <div style={footer}>
          <div>
            <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>◀</button>
            <span style={{ margin: "0 10px" }}>Página {currentPage} de {totalPages}</span>
            <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>▶</button>
          </div>
          <button onClick={handleFinalizar} style={finalizarBtn}>Finalizar</button>
        </div>
      </div>
    </div>
  );
}

// ── Componente auxiliar ──────────────────────────────────────────────────────
function Section({ label, value }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <p style={labelStyle}>{label}</p>
      <p style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: value && value !== "—" ? "#333" : "#bbb" }}>
        {value || "—"}
      </p>
    </div>
  );
}

// ── Estilos ──────────────────────────────────────────────────────────────────
const overlay = {
  position: "fixed", top: 0, left: 0,
  width: "100%", height: "100%",
  background: "rgba(0,0,0,0.4)",
  display: "flex", justifyContent: "center", alignItems: "center",
  zIndex: 9999,
};

const modal = {
  width: "min(98vw, 1000px)",
  maxHeight: "90vh",
  background: "#fff",
  borderRadius: 12,
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  display: "flex",
  flexDirection: "column",
};

const header = {
  background: "linear-gradient(135deg, #F2A66C 0%, #E8956A 100%)",
  padding: "12px 20px",
  color: "#fff",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const closeButton = {
  background: "transparent", border: "none",
  color: "#fff", cursor: "pointer", fontSize: 18,
  fontFamily: "'DM Sans', sans-serif",
};

const footer = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  padding: "12px 15px", borderTop: "1px solid #f0ebe6",
};

const finalizarBtn = {
  background: "#E8956A", border: "none",
  padding: "8px 16px", color: "#fff",
  borderRadius: 6, cursor: "pointer", fontWeight: 500,
};

const table = { width: "100%", borderCollapse: "collapse" };

const th = {
  padding: "12px 14px", textAlign: "left", fontSize: 12, fontWeight: 600,
  background: "#f7f4f1", borderBottom: "1px solid #ece7e2",
  color: "#666", fontFamily: "'DM Sans', sans-serif",
};

const td = {
  padding: "12px 14px", borderBottom: "1px solid #f1ece7",
  verticalAlign: "middle", fontFamily: "'DM Sans', sans-serif",
  fontSize: 13, color: "#444",
};

const statusButton = {
  width: 32, height: 32, borderRadius: "50%",
  border: "none", cursor: "pointer",
  fontSize: 14, fontWeight: "bold", transition: "0.2s",
};

const actionBtn = {
  padding: "8px 16px", borderRadius: 8,
  border: "none", cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600,
};

const inactiveStyle = { background: "#ececec", color: "#777" };
const approvedStyle = { background: "#D8F5DD", color: "#2F855A" };
const rejectedStyle = { background: "#FED7D7", color: "#C53030" };

const divider = { borderTop: "1px solid #f0ebe6", margin: "14px 0" };
const sectionTitle = { margin: "0 0 10px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 1 };
const labelStyle = { margin: "0 0 4px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 600, color: "#E8956A" };
